import { useEffect, useMemo, useState } from "react";
import PageHeader from "@/components/feature/PageHeader";
import SegmentedTabs from "@/components/feature/SegmentedTabs";
import CharacterCard from "@/components/feature/CharacterCard";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import { characters } from "@/mocks/characters";
import { characterTypeFilters, seasonFilters } from "@/mocks/site";
import useDocumentMeta from "@/hooks/useDocumentMeta";

const PAGE_SIZE = 8;

export default function Characters() {
  const [type, setType] = useState<string | number>("全部");
  const [season, setSeason] = useState<string | number>(0);
  const [page, setPage] = useState(1);

  useDocumentMeta({
    title: "美国禁忌4角色介绍 - 主要人物档案与关系解析",
    description:
      "美国禁忌4角色介绍列表，汇集主角、配角与反派的核心人物档案，可按角色类型与登场季数筛选，查看人物背景与关系网络。",
  });

  const filtered = useMemo(() => {
    return characters.filter((c) => {
      const typeOk = type === "全部" || c.type === type;
      const seasonOk = Number(season) === 0 || c.seasons.includes(Number(season));
      return typeOk && seasonOk;
    });
  }, [type, season]);

  useEffect(() => {
    setPage(1);
  }, [type, season]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const reset = () => {
    setType("全部");
    setSeason(0);
  };

  return (
    <div className="px-4 md:px-10 py-6 md:py-10">
      <PageHeader
        crumbs={[{ label: "首页", to: "/" }, { label: "角色介绍" }]}
        eyebrow="Characters"
        title="角色介绍"
        description="从调查记者到幕后操盘者，《美国禁忌4》塑造了一群动机各异、立场暧昧的人物。以下为登场的主要角色档案，点击任意角色可查看完整背景与人物关系。"
        meta={`当前展示 ${filtered.length} 位角色 · 共 ${totalPages} 页`}
      />

      <div className="flex flex-wrap gap-3 mb-6 md:mb-8">
        <SegmentedTabs
          ariaLabel="按角色类型筛选"
          options={characterTypeFilters}
          value={type}
          onChange={setType}
        />
        <SegmentedTabs
          ariaLabel="按登场季数筛选"
          options={seasonFilters}
          value={season}
          onChange={setSeason}
        />
      </div>

      {paged.length > 0 ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4 md:gap-5">
            {paged.map((c) => (
              <CharacterCard key={c.id} character={c} />
            ))}
          </div>
          <Pagination current={currentPage} total={totalPages} onChange={setPage} />
        </>
      ) : (
        <EmptyState
          title="没有找到匹配的角色"
          description="当前筛选条件下暂无角色资料，试试切换角色类型或登场季数，或重置全部筛选条件。"
          onReset={reset}
        />
      )}
    </div>
  );
}